import {
  Box,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip
} from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";

import Panel from "../../components/Panel";
import ClientSearchComponents, {
  CLIENT_SEARCH_BY,
  ClientSearchByType
} from "./ClientSearchComponents";
import { getBranchClients } from "../../api/client";
import { ageFormatter } from "../../helpers/formatters";
import { Client } from "../../interfaces/ClientInterface";
import { notificationAdd } from "../../actions/notification";
import { requestClientDataAccess } from "../../api/clientLink";
import { getClientGender } from "../Assessment/AssessmentClientSelect";
import { ResourceCentre } from "../../interfaces/ResourceCentreInterface";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import {
  ClientLinkRequestStatus,
  ClientLinkRequestStatusType
} from "../../interfaces/ClientLinkRequests";

interface PropType {
  onClose: () => void;
  rcFamily: Partial<ResourceCentre>[];
}

export type TBranchClients = Partial<Client> & {
  clientLinkRequestStatus: null | ClientLinkRequestStatusType;
};

function ClientLinkRequestStatusChip({ status }: { status: ClientLinkRequestStatusType | null }) {
  if (!status) return null;

  let color: "success" | "error" | "warning" = "warning";

  switch (status) {
    case ClientLinkRequestStatus.APPROVED:
      color = "success";
      break;
    case ClientLinkRequestStatus.REJECTED:
      color = "error";
      break;
    case ClientLinkRequestStatus.PENDING:
      color = "warning";
      break;
    default:
      color = "warning";
      break;
  }

  return <Chip label={status.toUpperCase()} color={color} variant="filled" size="small" />;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function SearchOtherBranchClients({ onClose, rcFamily }: PropType): React.ReactNode {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();

  const [loading, setLoading] = React.useState(false);
  const [isTyping, setIsTyping] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [clients, setClients] = React.useState<Array<TBranchClients>>([]);
  const [resourceCentreId, setResourceCentreId] = React.useState<number | null>(null);
  const [searchBy, setSearchBy] = React.useState<ClientSearchByType>(CLIENT_SEARCH_BY.NAME);
  const [requestSentForClients, setRequestSentForClients] = React.useState<
    Array<{
      id: number;
      resourceCentreId: number;
    }>
  >([]);

  const onRequestDataAccess = async (client: TBranchClients) => {
    if (!client?.id) return;

    // update the list of clients for which request has been sent
    setRequestSentForClients((prev) => {
      const presentClient = prev.find(
        (c) => c.id === client.id && c.resourceCentreId === client.resourceCentreId
      );

      if (
        !presentClient &&
        typeof client.id === "number" &&
        typeof client.resourceCentreId === "number"
      ) {
        return [...prev, { id: client.id, resourceCentreId: client.resourceCentreId }];
      }
      return prev;
    });

    // if current client request status is rejected, update to pending
    setClients((prev) =>
      prev.map((c) =>
        c.id === client.id
          ? { ...client, clientLinkRequestStatus: ClientLinkRequestStatus.PENDING }
          : c
      )
    );

    if (!rc?.id || !resourceCentreId || !client?.id) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: "Missing required fields"
        })
      );
    }

    try {
      const { success, message } = await requestClientDataAccess({
        requestingResourceCentreId: rc.id,
        destinationResourceCentreId: Number(resourceCentreId),
        clientId: client.id
      });

      if (success) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            autoTimeout: true,
            message: message || "Sent request successfully"
          })
        );
      }
    } catch (err) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          autoTimeout: true,
          message: err.message || err.data.message || "Error sending data access message"
        })
      );
    }
  };

  useEffect(() => {
    if (!searchValue || !searchBy || !resourceCentreId) return;

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await getBranchClients({ resourceCentreId, searchValue, searchBy });
        setClients(data);
      } catch (err) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "error",
            message: err?.message || err?.data?.message || "Something went wrong!",
            autoTimeout: true
          })
        );
      } finally {
        setLoading(false);
        setIsTyping(false);
      }
    }, 500);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [dispatch, resourceCentreId, searchBy, searchValue]);

  return (
    <Panel onClose={onClose} title="Search Branch Clients">
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center"
          }}
        >
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel id="rc-select-label" shrink sx={{ backgroundColor: "white", px: 0.5 }}>
              Resource Centre
            </InputLabel>
            <Select
              labelId="rc-select-label"
              value={resourceCentreId}
              onChange={(e) => setResourceCentreId(Number(e.target.value))}
              label="Resource Centre"
              variant="outlined"
            >
              {rcFamily.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ClientSearchComponents
            value={searchValue}
            loading={loading}
            debounceLoading={isTyping}
            onDebounceLoadingChange={setIsTyping}
            onInputClear={() => setSearchValue("")}
            onSearchByChange={(value) => setSearchBy(value as ClientSearchByType)}
            onInputChange={(value) => {
              setIsTyping(true);
              setSearchValue(value);
            }}
            sxProps={{
              width: "100%"
            }}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && clients.length > 0 && (
            <List>
              {clients.map((client) => (
                <ListItem key={client.id} divider sx={{ px: 0, cursor: "pointer" }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Typography>
                          {client?.firstName} {client?.lastName}
                        </Typography>
                        <ClientLinkRequestStatusChip status={client?.clientLinkRequestStatus} />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        {client?.email && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {client.email}
                            </Typography>
                            {(client?.phone || client?.dob || client?.gender) && (
                              <Divider orientation="vertical" flexItem />
                            )}
                          </>
                        )}

                        {client?.phone && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {client.phone}
                            </Typography>
                            {(client?.dob || client?.gender) && (
                              <Divider orientation="vertical" flexItem />
                            )}
                          </>
                        )}

                        {client?.dob && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {ageFormatter(client.dob as Date)}
                            </Typography>
                            {client?.gender && <Divider orientation="vertical" flexItem />}
                          </>
                        )}

                        {client?.gender && (
                          <Typography variant="body2" color="text.secondary">
                            {getClientGender(client.gender)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => onRequestDataAccess(client)}
                    disabled={
                      client?.clientLinkRequestStatus === ClientLinkRequestStatus.APPROVED ||
                      client?.clientLinkRequestStatus === ClientLinkRequestStatus.PENDING ||
                      !!requestSentForClients.find(
                        (c) => c.id === client.id && c.resourceCentreId === client.resourceCentreId
                      )
                    }
                  >
                    Request Access
                  </Button>
                </ListItem>
              ))}
            </List>
          )}

          {!loading && searchValue && clients.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No clients found by <strong>{searchBy}</strong> for &quot;
              <strong>{searchValue}</strong>&quot;
            </Typography>
          )}
        </Box>
      </Box>
    </Panel>
  );
}
